// Source of truth for the Heart Tree update: its name, version and pets.
// 12 pets total; 8 have GIFs and surface in the home hero. The other 4 are
// PNG-only and stay hidden from the hero (still discoverable through Catalog
// once the backend rows ship).

export const RELEASE_TAG = 'may2026';

// Shown wherever the update is named, so a rename happens here only.
export const RELEASE_NAME = 'The Heart Tree Update';
export const RELEASE_SHORT = 'Heart Tree';
export const RELEASE_VERSION = '0.7.0';

const ASSET_BASE = '/images/pets/release/may2026';

export const RELEASE_PETS = [
  {
    id: 'hermit_crab',
    name: 'Hermit Crab',
    gif: `${ASSET_BASE}/hermit_crab.gif`,
    still: `${ASSET_BASE}/hermit_crab.png`,
    blurb: 'A tide-line scavenger that drags its shell home wherever you go.',
  },
  {
    id: 'nimbus_pup',
    name: 'Nimbus Pup',
    gif: `${ASSET_BASE}/nimbus_pup.gif`,
    moods: [
      `${ASSET_BASE}/nimbus_pup.gif`,
      `${ASSET_BASE}/nimbus_pup_alt.gif`,
      `${ASSET_BASE}/nimbus_pup_alt2.gif`,
    ],
    blurb: 'A featherweight cloud pup that drifts beside you in three moods.',
  },
  {
    id: 'shadowling',
    name: 'Shadowling',
    gif: `${ASSET_BASE}/shadowling.gif`,
    still: `${ASSET_BASE}/shadowling.png`,
    blurb: 'A loyal half-wolf, half-shade that watches your back at night.',
  },
  {
    id: 'pocket_phoenix',
    name: 'Pocket Phoenix',
    gif: `${ASSET_BASE}/pocket_phoenix.gif`,
    blurb: 'A palm-sized firebird that hatches over and over again.',
  },
  {
    id: 'clockwork_sentinel',
    name: 'Clockwork Sentinel',
    gif: `${ASSET_BASE}/clockwork_sentinel.gif`,
    blurb: 'A windup guardian, polished brass and steady ticking.',
  },
  {
    id: 'star_sprite',
    name: 'Star Sprite',
    gif: `${ASSET_BASE}/star_sprite.gif`,
    blurb: 'A wandering pinprick of starlight that hums when it is happy.',
  },
  {
    id: 'lantern_imp',
    name: 'Lantern Imp',
    gif: `${ASSET_BASE}/lantern_imp.gif`,
    blurb: 'A cheeky imp lugging a tiny lantern of its own warm light.',
  },
  {
    id: 'duck_parade',
    name: 'Duck Parade',
    gif: `${ASSET_BASE}/duck_parade.gif`,
    blurb: 'One mama duck, a wobbling line of ducklings, zero discipline.',
  },
];

// A seasonal guest in the hero: the free Cat in its Witch Hat, riding a broom.
// Not a new species, so it stays out of RELEASE_PET_IDS and the catalog rows
// and its card links to the Cat's own page.
export const SEASONAL_PET = {
  id: 'cat',
  name: 'Witch Cat',
  pill: 'Halloween',
  gif: `${ASSET_BASE}/witch_cat.gif`,
};

// PNG-only pets - discoverable via /catalog but intentionally absent from the
// home hero until they have proper animation passes. has3d=true means a
// Bedrock .geo.json + texture is staged under public/pets/ and the detail
// page should render a rotating 3D preview as the primary visual.
export const RELEASE_PETS_PNG_ONLY = [
  { id: 'mocchi_rabbit',   name: 'Mocchi Rabbit', has3d: true },
  { id: 'mini_gryphon',    name: 'Mini Gryphon',  has3d: true },
  { id: 'pigmy_kraken',    name: 'Pigmy Kraken',  has3d: true },
  { id: 'jellyfish_bloom', name: 'Jellyfish Bloom', has3d: true, still: `${ASSET_BASE}/jellyfish_bloom.png` },
];

export const RELEASE_PET_IDS = new Set([
  ...RELEASE_PETS.map((p) => p.id),
  ...RELEASE_PETS_PNG_ONLY.map((p) => p.id),
]);

export const RELEASE_PET_3D_IDS = new Set(
  RELEASE_PETS_PNG_ONLY.filter((p) => p.has3d).map((p) => p.id),
);

export const RELEASE_COSMETIC_COUNT = 30;

// Cosmetic IDs with hand-rendered preview screenshots staged under
// public/cosmetics/previews/. These look better than the 3D Bedrock
// renderer and beat the 2D SVG fallback for detail pages.
export const RELEASE_COSMETIC_PREVIEWS = {
  scarf:               '/cosmetics/previews/scarf.gif',
  moth_wing_cape:      '/cosmetics/previews/moth_wing_cape.gif',
  moon_wizard_hat:     '/cosmetics/previews/moon_wizard_hat.gif',
  crossed_sword:       '/cosmetics/previews/crossed_sword.png',
  turtle_shell_pack:   '/cosmetics/previews/turtle_shell_pack.png',
  pocket_lute:         '/cosmetics/previews/pocket_lute.png',
  propeller_pack:      '/cosmetics/previews/propeller_pack.gif',
  rainbow_sneakers:    '/cosmetics/previews/rainbow_sneakers.gif',
  strapped_banjo:      '/cosmetics/previews/strapped_banjo.png',
  mini_cauldron:       '/cosmetics/previews/mini_cauldron.png',
  phoenix_helm:        '/cosmetics/previews/phoenix_helm.png',
  ballerina_slippers:  '/cosmetics/previews/ballerina_slippers.gif',
  moon_boots:          '/cosmetics/previews/moon_boots.gif',
  lollipop_staff:      '/cosmetics/previews/lollipop_staff.gif',
  wizard_quill:        '/cosmetics/previews/wizard_quill.gif',
  tea_cup:             '/cosmetics/previews/tea_cup.gif',
  royal_crown:         '/cosmetics/previews/royal_crown.gif',
  heart_monocle:       '/cosmetics/previews/heart_monocle.png',
  rage_helmet:         '/cosmetics/previews/rage_helmet.png',
  black_sunglasses:    '/cosmetics/previews/black_sunglasses.gif',
  gentlemen_bowtie:    '/cosmetics/previews/gentlemen_bowtie.gif',
  clockwork_collar:    '/cosmetics/previews/clockwork_collar.gif',
  bell_necklace:       '/cosmetics/previews/bell_necklace.gif',
  luminous_choker:     '/cosmetics/previews/luminous_choker.gif',
};

// Curated set of cosmetic IDs used for the home release strip thumbnails.
// Leads with cosmetics that have rendered previews, then fills with SVG
// classics for variety across head, eyes, neck, back, cape, and weapon.
export const RELEASE_COSMETIC_SHOWCASE = [
  'phoenix_helm',
  'royal_crown',
  'moon_wizard_hat',
  'tea_cup',
  'moth_wing_cape',
  'propeller_pack',
  'turtle_shell_pack',
  'crossed_sword',
  'lollipop_staff',
  'ballerina_slippers',
  'moon_boots',
  'mini_cauldron',
];
